// Small data-loading hook used by every data view. Runs `loader` on mount and
// whenever `deps` change, tracking loading/error so a view can render a spinner,
// a retryable error, or its content — never a silent blank. `reload()` re-runs
// the loader (used to refresh after a mutation).
//
// On a reload the previous `data` is kept until the new result lands, so views
// guard the full-screen spinner with `loading && data === undefined` (initial
// load only) — a post-mutation refresh updates in place without a flash.
import { useCallback, useEffect, useRef, useState } from "react";

export interface Async<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): Async<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Keep the latest loader without making it a dependency — callers pass an
  // inline closure, so only `deps` (and the reload tick) should retrigger.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    loaderRef.current()
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, reload };
}
